/*PDP information accordion behaviour (ECSR-643).

The disclosures now carry a shared name attribute, which is what makes the
browser collapse whichever sibling was open when a new one is expanded. This
file covers the three things that grouping alone does not:

1. Scroll anchoring. Collapsing a section ABOVE the one just opened takes its
   height out of the page, which drags the header the shopper just tapped up
   the viewport — further the deeper into the accordion they are working. The
   header is pulled back only as far as it takes to clear the sticky site
   header. Restoring its exact previous offset instead would be worse: a header
   tapped near the bottom of the viewport would be pinned there, pushing the
   content the shopper just asked for below the fold.
2. Assistive-technology state. global.js puts role="button" on these summaries,
   which replaces the native disclosure semantics, so its hand-managed
   aria-expanded becomes the only signal a screen reader gets. It only updates
   the summary that was clicked, so a section the browser collapses on its own
   would keep reporting itself as expanded. Every summary in the group is
   resynced from the disclosure's real state instead.
3. A fallback for browsers predating exclusive-accordion support, where the
   shared name is ignored and every section would still open at once.*/

(function () {
  'use strict';

  const INIT_FLAG = '__jaProductAccordionSingleOpen';
  const CONTAINER_SELECTOR = '.product-tabs-container';
  const HEADER_SELECTOR = '.section-header';
  const SAFE_MARGIN_PX = 8;

  if (window[INIT_FLAG]) {
    return;
  }
  window[INIT_FLAG] = true;

  const supportsExclusive = 'name' in document.createElement('details');

  function closeSiblings(container, openDetails) {
    const open = container.querySelectorAll('details[open]');
    for (let i = 0; i < open.length; i += 1) {
      if (open[i] !== openDetails) {
        open[i].open = false;
      }
    }
  }

  function syncExpandedState(container) {
    const summaries = container.querySelectorAll('details > summary');
    for (let i = 0; i < summaries.length; i += 1) {
      const summary = summaries[i];
      const isOpen = summary.parentNode.hasAttribute('open');
      summary.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }
  }

  /*The highest the tapped header may sit and still be clear of the site header.

    The site header reveals on any upward scroll, and the correction below is an
    upward scroll — so it can bring the header back on its way past. It also
    hides by moving to a negative top rather than leaving the page. Its height is
    therefore reserved whenever it is sticky at all, not only while it happens to
    be on screen, otherwise the summary is parked exactly where the header is
    about to reappear and ends up underneath it.*/
  function minimumVisibleTop() {
    const header = document.querySelector(HEADER_SELECTOR);
    if (!header) {
      return SAFE_MARGIN_PX;
    }

    const position = window.getComputedStyle(header).position;
    if (position !== 'sticky' && position !== 'fixed') {
      return SAFE_MARGIN_PX;
    }

    const rect = header.getBoundingClientRect();
    const reserved =
      Math.max(rect.bottom, header.offsetHeight) + SAFE_MARGIN_PX;

    /*This figure drives a scroll, so cap it. A header taller than a third of the
      viewport is not a state this theme reaches today, but it could arrive from
      the theme editor without touching this file, and the failure would be a
      silent lurch up the page on every tap.*/
    return Math.min(reserved, window.innerHeight / 3);
  }

  /*Delegated from the document so an accordion rendered after load is covered
    without binding a second listener. Activating a summary from the keyboard
    dispatches a click too, so Enter and Space arrive here as well.*/
  document.addEventListener('click', function (event) {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') {
      return;
    }

    const summary = target.closest('summary');
    if (!summary) {
      return;
    }

    const container = summary.closest(CONTAINER_SELECTOR);
    const details = summary.closest('details');
    if (!container || !details) {
      return;
    }

    /*Measure after the browser has applied the toggle this click causes, so
      the sibling that closed has already left the layout.*/
    window.requestAnimationFrame(function () {
      if (!supportsExclusive && details.open) {
        closeSiblings(container, details);
      }

      syncExpandedState(container);

      const top = summary.getBoundingClientRect().top;
      const minimumTop = minimumVisibleTop();
      if (top < minimumTop) {
        window.scrollBy(0, top - minimumTop);
      }
    });
  });
})();
